import { makeAutoObservable, runInAction } from 'mobx'
import {
  type IHubspotSettingsDict,
  type IIntegrationSettingsProperties,
  type Integration,
  IntegrationKey,
} from 'entities/Integrations'
import { type CustomField, CustomFieldsStore, createCustomFieldPromise } from 'entities/CustomField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import type {
  SettingsPropertyItemType,
  SettingsPropertyOptionType,
} from 'pages/settings/pages/integrations/pages/integrationSettings/model/types'
import type { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import { SettingsProperty } from 'pages/settings/pages/integrations/pages/integrationSettings/model/SettingsProperty'

const FIELDS_LIMIT = 250

type SettingsWithPropertiesMapping = { properties_mapping: IIntegrationSettingsProperties[] }

export class IntegrationSettingsCustomPropertyStore<
  T extends SettingsWithPropertiesMapping = IHubspotSettingsDict
> {
  private _properties = new Map<number | string, SettingsProperty<string, T>>()
  private _customFields = new CustomFieldsStore(FIELDS_LIMIT)
  private _customOptions: SettingsPropertyOptionType[] = []
  private _usedOptionsIds = new Set<string | number>()

  constructor(
    private _integration: Integration,
    private _customSetting: IntegrationCustomSettingsStore<T>,
    private _bannedIds: string[],
    private _entities: IEntitiesStore,
    private _withoutPropertiesMapping: boolean = false
  ) {
    makeAutoObservable(this)
  }

  async init() {
    this._customFields.loadData()

    const fullDataTimeout = setInterval(() => {
      if (!this.customFieldsLoading) {
        clearInterval(fullDataTimeout)
        this._customFieldsInitialization()
      }
    }, 200)
  }

  private _customFieldsInitialization = async () => {
    const data = await this._entities.mergeFieldStore.getIntegrationFields(this._integration.key)

    runInAction(() => {
      this._customOptions = data.map((item) => {
        // Unique name for fields with the same name but different object types
        const uniqueName = item.referencedObjectType
          ? `${item.name}_${item.referencedObjectType}`
          : item.name

        return {
          id: uniqueName,
          label: item.label,
          name: item.name,
          value: uniqueName,
          type: item.type,
          groupName: item.referencedObjectType,
        }
      })
    })

    const getField = (value: IIntegrationSettingsProperties) => {
      return this._customFieldsAllMap.get(value.custom_field_name)
    }

    const list = this._customSetting.getValue('properties_mapping')

    // TODO: remove this after add new requests on the backend
    if (!list) return
    const filteredList = list.filter((item) => !!getField(item))
    this._customSetting.setValue('properties_mapping', filteredList)

    filteredList?.forEach((item) => {
      const field = getField(item)

      const customOption = this._customOptions.find((option) => {
        if (option.groupName) {
          return (
            option.name === item.integration_field_name &&
            option.groupName.toLowerCase() === item.integration_object_type?.toLowerCase()
          )
        }
        return option.name === item.integration_field_name
      })

      if (field) {
        this._setProperty({
          id: item.custom_field_name,
          fieldName: field.label,
          value: customOption?.value || '',
          disabled: false,
          type: 'custom',
          fieldType: field.typeCustom,
          options: this._customOptions,
          integrationName: this._integration.name,
        })
      }
    })
  }

  private _isPropertiesMappingUnchanged = (): boolean => {
    const propertiesMapping = this._customSetting.getValue('properties_mapping')
    return (
      propertiesMapping?.every(
        ({ custom_field_name, integration_field_name, integration_object_type }) => {
          const property = this._properties.get(custom_field_name)
          if (!property) return false

          // Find matching option considering both grouped and non-grouped cases
          const option = this._customOptions.find((opt) => {
            if (integration_object_type) {
              return (
                opt.name === integration_field_name &&
                opt.groupName?.toLowerCase() === integration_object_type.toLowerCase()
              )
            } else {
              return opt.name === integration_field_name && !opt.groupName
            }
          })

          return property.value === option?.value
        }
      ) ?? false
    )
  }

  private _patchCustomParams_1_0 = async (patchAll = false) => {
    if (!patchAll && this._isPropertiesMappingUnchanged()) {
      // mock success status
      return { status: 200 }
    }

    const params = this.rows.map((item) => {
      return {
        custom_field_name: String(item.id),
        integration_field_name: String(item.value),
      }
    })

    return this._customSetting.patchMappingData_1_0('properties_mapping', params)
  }

  private _patchCustomParams = async (patchAll = false) => {
    const integrationFields = this._entities.mergeFieldStore.mergeFieldsIntegrationsMap.get(
      this._integration.key
    )
    const customProperties = this.rows.reduce((acc, item) => {
      const checkTemporaryFile = patchAll || item.type !== 'temporary'
      if (item.type !== 'default' && item.value && checkTemporaryFile) {
        const integrationField = integrationFields?.get(item.value)
        const propertyMapping: IIntegrationSettingsProperties = {
          custom_field_name: item.id as string,
          integration_field_name: integrationField?.name || '',
          integration_field_type: integrationField?.type || '',
        }
        // For integrations with multiple object types, we need to include the object type e.g. (Contact/Lead)
        if (integrationField?.referencedObjectType) {
          propertyMapping.integration_object_type = integrationField.referencedObjectType
        }
        acc.push(propertyMapping)
      }
      return acc
    }, [] as IIntegrationSettingsProperties[])

    // avoiding server request for temporary fields
    if (!patchAll && this._isPropertiesMappingUnchanged()) {
      // mock success status
      return { status: 200 }
    }

    return await this._customSetting.patchData([
      {
        key: 'properties_mapping' as keyof T,
        value: customProperties as T[keyof T],
      },
    ])
  }

  private _patchData = async (
    newData: SettingsPropertyItemType<string, T>,
    is_1_0_request?: boolean
  ) => {
    const item = this._properties.get(newData.id)
    if (!item) return
    this._setProperty(newData)

    const response = await (is_1_0_request
      ? this._patchCustomParams_1_0()
      : this._patchCustomParams())

    if (!(response?.status === 200)) {
      this._setProperty(item)
    }
  }

  private _deleteData = async (data: SettingsPropertyItemType<string, T>) => {
    runInAction(() => {
      this._usedOptionsIds.delete(data.value || '')
      this._properties.delete(data.id)
    })
  }

  private _setProperty = (data: SettingsPropertyItemType<string, T>) => {
    runInAction(() => {
      this._properties.set(
        data.id,
        new SettingsProperty<string, T>(data, {
          onDelete: this._deleteData,
          onChange: this._patchData,
          initialBannedIds: this._usedOptionsIds,
        })
      )
    })
    this._updateBannedIds()
  }

  private _updateBannedIds = () => {
    runInAction(() => {
      this._usedOptionsIds.clear()
      this._bannedIds.forEach((key) => {
        this._usedOptionsIds.add(key)
      })
      this._properties.forEach((item) => {
        this._usedOptionsIds.add(item.value || '')
      })
    })
  }

  refreshStore = () => {
    this._properties.clear()
    this._customFieldsInitialization()
  }

  searchFields = (term = '') => {
    this._customFields.searchFields(term)
  }

  clearSearch = () => {
    this._customFields.clearSearch()
  }

  createField = async () => {
    const field = await createCustomFieldPromise()
    if (!field) return null

    await this._customFields.reloadData()

    return field
  }

  addNewProperty = (field: CustomField) => {
    this._setProperty({
      id: field.key,
      fieldName: field.name,
      value: null,
      disabled: false,
      type: 'temporary',
      fieldType: field.type,
      options: this._customOptions,
      integrationName: this._integration.name,
    })
  }

  saveNewProperties = async (is_1_0_request?: boolean) => {
    if (this.isValidForm) {
      await (is_1_0_request ? this._patchCustomParams_1_0(true) : this._patchCustomParams(true))
      this.refreshStore()
    } else {
      this._properties.forEach((item) => {
        if (!item.mapped) {
          this._setProperty({ ...item.original, showValidation: true })
        }
      })
    }
  }

  get withoutPropertiesMapping() {
    return this._withoutPropertiesMapping
  }

  private get _customFieldsAllMap() {
    const customFieldsMap = this._entities.mergeFieldStore.getMergeFieldsIntegration(
      IntegrationKey.salesmessage
    )

    if (!customFieldsMap) return new Map<string, MergeField>()

    return customFieldsMap
  }

  get customFields() {
    return this._customFields.termFields
      .reduce(
        (acc, field) => {
          const targetFields = field.isCustom ? acc[1] : acc[0]
          targetFields.fields.push(field)
          return acc
        },
        [
          {
            id: 'defaultFields',
            groupName: 'Default fields',
            fields: [] as CustomField[],
          },
          {
            id: 'customFields',
            groupName: 'Custom fields',
            fields: [] as CustomField[],
          },
        ]
      )
      .filter((acc) => acc.fields.length)
  }

  get rows() {
    return Array.from(this._properties.values())
  }

  get mappedLength() {
    return this.rows.filter((item) => item.mapped).length
  }

  get isValidForm() {
    return this.mappedLength === this.rows.length
  }

  get customFieldsLoading() {
    return (
      this._customFields.loading ||
      this._entities.mergeFieldStore.loading ||
      this._customSetting.status.isIdle ||
      this._customSetting.status.isLoading
    )
  }

  get loading() {
    return this._customSetting.status.isLoading
  }

  get isExistNewProperties() {
    return this.rows.some((item) => item.type === 'temporary')
  }

  get isRemovedOldProperties() {
    return !!this._customSetting
      .getValue('properties_mapping')
      ?.some((item) => !Boolean(this._properties.get(item.custom_field_name)))
  }

  get integrationName() {
    return this._integration?.name || ''
  }
}
